/* eslint-disable max-len */
import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { SplitH1, SplitH2, useSanitizedTranslation, useScrolledTheme } from 'ui';
import Image from 'next/image';

const variants = {
  initial: {
    opacity: 0,
    y: 40,
    scale: 0.9,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      staggerChildren: 0.03,
      delayChildren: 0.3,
    },
  },
};

const heading = 'Meet our end-to-end FAST solution';
const body =
  'Since its inception in 2011, our tech stack has continued to evolve and revolutionize linear and VOD distribution methods. Our FAST solutions meet the needs of content, platform and advertiser partners around the world.';

const Cog: React.FC<{ icon?: string; fill?: string; message: string; side: string; invert?: boolean }> = ({
  icon = '/svg/bulb.svg',
  fill = '#4DA1A3',
  message,
  side,
  invert,
}) => {
  const { t } = useSanitizedTranslation();

  return (
    <motion.div variants={variants} className="cog relative flex justify-center">
      {side === 'top' && (
        <div
          className={`type-body weight-500 absolute top-3 flex w-[250px] flex-col items-center text-sm`}
          style={{
            transform: 'translateY(-100%)',
          }}
        >
          <div
            className="p-4"
            data-i18n
            style={{
              background: invert ? fill : 'transparent',
              color: invert ? '#ffffff' : '#000000',
            }}
          >
            {t(message)}
          </div>
          <div className="h-[50px] w-[2px]" style={{ background: fill }} />
        </div>
      )}
      <div className="flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 62" className="cog-rotate h-[120px] w-[120px]">
          <path
            fill={fill}
            fillRule="evenodd"
            d="M10.6 33.7v-1.4l-.1-.7V29l.3-1.7V27l.1-.3.2-.7.2-1 .2-.4.5-1.4.6-1.3.3-.7.5-.8.4-.7.5-.7c.4-.4.6-.9 1-1.2l.9-1s0-.2.3-.3l.7-.7 1.2-1 .6-.5.7-.5 1.5-.9 1.3-.6 1-.4.3-.2.4-.1 1-.3 1.4-.4 1.7-.3h.8l.9-.1h1.5a196.2 196.2 0 0 0 2.8.2l.7.1.8.2.8.2.4.1.4.2 1.6.5 1.3.7 1 .5a21.9 21.9 0 0 1 .6.4l.9.6 1.2.8 1.2 1.2.6.6.6.6 1 1.2.6.8.2.3.7 1.3c.3.3.4.8.7 1.3l.3.7.3.8.3.8.3.8.3 1.5a440.8 440.8 0 0 1 .3 1.8l.1 1V33l-.3 1.7v.4l-.1.3-.2.7-.2 1-.2.4-.5 1.4-.6 1.3-.3.7-.5.8-.4.7-.5.7c-.4.4-.6.9-1 1.2l-.9 1s0 .2-.3.3l-.7.7-1.2 1-.6.5-.7.5-1.5.9-1.3.6-1 .4-.3.2-.4.1c-.3.1-.6.3-1 .3l-1.4.4-1.7.3H33l-.9.1h-2.9l-1.4-.2-.7-.1-.8-.2-.8-.2-.4-.1-.4-.1L23 50l-1.3-.7-1-.5-.3-.2-.3-.2-.9-.5-1.2-1-1.2-1-.6-.7-.6-.6-1-1.1-.6-.9-.2-.3-.7-1.2-.7-1.4-.3-.7-.3-.8-.3-.8-.3-.8L11 35l-.3-1.4M.1 28.1 0 30.8a114.6 114.6 0 0 0 2.4.7l2.4.6a28.4 28.4 0 0 0 .4 3.5l.1.9.1.3L3.3 38 1 39.3l.3.8c0 .5.3 1.1.6 1.7l.6 1.7.4.8 2.5-.4 2.4-.5.1.3.5.7a2465 2465 0 0 0 .8 1.3l.5.7.7 1-1.5 2-1.3 2 .6.7.5.6.4.3a23.5 23.5 0 0 1 1.7 1.6 55 55 0 0 1 .6.5l2-1.4 2-1.5a321.3 321.3 0 0 0 3 2l1.1.5-.4 2.4a65.8 65.8 0 0 0-.1 2.6s.3 0 .5.2l1.7.6 1.8.5.8.2 1.2-2.2 1.1-2.2 2.4.4H31l.6 2.5.8 2.3h.9l1.8-.2 1.8-.3.6-.1h.2l.1-2.6v-2.5L39 56l1.1-.4 1.2-.4 1-.5s.8 1 1.7 1.8c.8 1 1.7 1.8 1.7 1.8h.2l.6-.4 1.5-1 .4-.2.4-.3.7-.5.7-.5-1-2.3a54 54 0 0 0-1-2.3l.9-.8.6-.5.2-.3.8-.9.6-.6.2-.3a62 62 0 0 0 4.6 1.8l.5-.7 1-1.5.9-1.6.3-.7s-.9-1-1.8-1.7l-2-1.6.2-.3.3-.8.4-1.1.7-2.3 2.4-.2 1.7-.1.8-.1.4-2.6.1-2.7-.7-.2-1.7-.5-2.4-.6-.2-2.4s0-.6-.2-1.1l-.1-.9-.1-.3 2.1-1.2 2.2-1.3-.3-.8c0-.5-.3-1.1-.6-1.7l-.6-1.7-.4-.8-2.5.4-2.3.6-.2-.4-.5-.7-.6-1-.2-.3-.5-.7-.7-1 1.5-2 1.3-2-.6-.7-.5-.6-.4-.3-.3-.4L51 7.4l-.4-.4-.2-.1-2 1.4-2 1.5-1-.7-1-.6-1-.6-1.1-.5.4-2.5.3-2.5H43l-.5-.3-1.7-.6-1.8-.5-.8-.2L36.9 3l-1.1 2.2-2.4-.4H31l-.6-2.4-.8-2.4h-.9L27 .3l-1.8.3-.6.1h-.2l-.1 2.6v2.5L23 6l-1.1.4-1.2.4-1 .5L18 5.5c-.8-1-1.7-1.8-1.7-1.8H16l-.5.4-1.6 1-.4.2-.4.3-.7.5-.7.5a60.4 60.4 0 0 0 2 4.6l-.9.8-.6.5-.2.3-.8.9-.6.6-.2.3-2.3-1-2.3-.8-.5.7-1 1.5-.9 1.6-.3.7a59 59 0 0 0 3.7 3.3l-.1.3-.3.8a195.8 195.8 0 0 0-1 3.4s-1.3 0-2.5.2l-1.7.1-.8.1-.4 2.6"
          />
        </svg>
        <Image src={icon} className="absolute" alt="" width={40} height={40} />
      </div>
      {side === 'bottom' && (
        <div
          className={`type-body weight-500 absolute bottom-3 flex w-[250px] flex-col items-center p-2 text-sm`}
          style={{
            transform: 'translateY(100%)',
          }}
        >
          <div className="h-[50px] w-[2px]" style={{ background: fill }} />
          <div
            className="p-4"
            data-i18n
            style={{
              background: invert ? fill : 'transparent',
              color: invert ? '#ffffff' : '#000000',
            }}
          >
            {t(message)}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export const TechStackBlock: React.FC<{ headingOne?: boolean }> = ({ headingOne }) => {
  const { t } = useSanitizedTranslation();
  const sectionRef = useRef();

  const Heading = headingOne ? SplitH1 : SplitH2;

  const ref = useRef<any>();
  const { componentInView, colors } = useScrolledTheme({
    ref,
    background: '#E0E7ED',
    accent: '#2AAC00',
    text: '#000000',
  });

  return (
    <>
      <motion.section
        viewport={{ once: true, margin: '-25% 0% -25% 0%' }}
        variants={variants}
        whileInView="animate"
        initial="initial"
        ref={ref}
        className="w-full flex-col py-16 text-left lg:py-24"
      >
        <div className="container relative mx-auto flex flex-col items-center px-8 lg:text-center">
          <Heading variants={variants} content={heading} className={`${headingOne ? 'type-title' : 'type-heading'} z-10 flex flex-col`} />
          {body && <motion.div variants={variants} className="type-body py-4 lg:w-1/2" data-i18n dangerouslySetInnerHTML={{ __html: t(body) }} />}

          <div className="max-xl:align-start relative flex w-full justify-center">
            <div className="absolute left-0 top-0 z-10 h-full w-[20px] xl:hidden" style={{ background: 'linear-gradient(to right, #E0E7EDFF, #E0E7ED00)' }} />
            <div className="cogs relative flex w-full space-x-4 overflow-y-hidden pb-[350px] pt-[200px] max-xl:overflow-x-scroll max-xl:px-[135px] max-md:px-[100px] xl:justify-center">
              <div className="relative flex space-x-4">
                <Cog invert icon="/svg/cal.svg" message="Ingest and store video and metadata packages" side="top" />
                <Cog icon="/svg/cloud.svg" message="Program and schedule linear FAST channels." side="bottom" />
                <div
                  data-i18n="Content Management<br/>System"
                  className="absolute -left-4 bottom-[-250px] flex h-[75px] w-full items-center rounded-md bg-[#4DA1A3] p-4 text-left text-white xl:justify-center xl:text-center"
                  dangerouslySetInnerHTML={{ __html: t('Content Management<br/>System') }}
                />
              </div>
              <div className="relative flex space-x-4">
                <Cog fill="#0098FF" icon="/svg/user-tree.svg" message="Feed FAST channels through SSAI stack with 50+ demand partners." side="top" />
                <Cog
                  fill="#0098FF"
                  icon="/svg/upload.svg"
                  message="Export FAST channels with SSAI to over 18 custom distribution endpoints around the world."
                  side="bottom"
                />

                <Cog
                  fill="#0098FF"
                  icon="/svg/revenue.svg"
                  message="Effectively and efficiently monetize FAST channels through 50+ ad demand partners. Pre-fetching supported."
                  side="top"
                />

                <Cog fill="#0098FF" icon="/svg/strategy.svg" message="Multiple business models supported to meet your revenue goals." side="bottom" />
                <div
                  data-i18n="SSAI Stack<br/>ft. Ads Management System"
                  className="absolute -left-4 bottom-[-250px] flex h-[75px] w-full items-center rounded-md bg-[#0098FF] p-4 text-left text-white xl:justify-center xl:text-center"
                  dangerouslySetInnerHTML={{ __html: t('SSAI Stack<br/>ft. Ads Management System') }}
                />
              </div>

              <div className="relative flex space-x-4">
                <Cog
                  invert
                  fill="#155772"
                  icon="/svg/bulb.svg"
                  message="Custom and actionable playout insights provided through dedicated dashboards"
                  side="top"
                />
                <div
                  data-i18n
                  className="absolute -left-4 bottom-[-250px] flex h-[75px] w-full items-center rounded-md bg-[#155772] p-4 text-left text-white xl:justify-center xl:text-center"
                  dangerouslySetInnerHTML={{ __html: t('Data Intelligence') }}
                />
              </div>
            </div>
            <div className="absolute right-0 top-0 z-10 h-full w-[30px] xl:hidden" style={{ background: 'linear-gradient(to left, #E0E7EDFF, #E0E7ED00)' }} />
          </div>
        </div>
      </motion.section>
    </>
  );
};
